import React from 'react';
import {
  Dialog, DialogContent, DialogHeader, DialogTitle, DialogFooter, DialogDescription,
} from '@/components/ui/dialog';
import { Button } from '@/components/ui/button';
import { KeyRound, RefreshCw, Copy, CheckCircle2, AlertCircle, AlertTriangle } from 'lucide-react';
import { toast } from 'sonner';
import { agentAPI } from '@/lib/api';
import { getErrorMessage } from '@/lib/utils';

/**
 * Phase 27 / 27.1 — Portal-access dialog with two modes.
 *
 *   mode='generate'   → creates a brand-new portal user (first activation).
 *   mode='regenerate' → resets an already-activated user's password back
 *                       to their NPN. Requires an existing account.
 *
 * Both modes:
 *   * Use the agent's NPN as the temporary password.
 *   * Flag the user with `force_password_change=true` — the existing
 *     ForcePasswordChangeModal on the frontend forces a rotation on
 *     the next login.
 *   * Show the temp password inline for the KR admin (+ Copy button)
 *     so a bounced email can be recovered by walking up to a phone.
 *
 * Regenerate shows an amber warning banner so the KR admin knows they
 * are overwriting an active password.
 */
export const GenerateAccessDialog = ({
  open,
  onOpenChange,
  agent,
  linkedUser,
  mode = 'generate',
  onGenerated,
}) => {
  const [busy, setBusy] = React.useState(false);
  const [result, setResult] = React.useState(null);

  React.useEffect(() => { if (open) setResult(null); }, [open]);

  const isRegen = mode === 'regenerate';
  const Icon = isRegen ? RefreshCw : KeyRound;

  const handleAction = async () => {
    if (!agent?.npn) return;
    setBusy(true);
    try {
      const call = isRegen ? agentAPI.regenerateAccess : agentAPI.generateAccess;
      const res = await call(agent.npn);
      setResult(res.data);
      const isHealed = !!res.data?.healed;
      const successMsg = isRegen
        ? (res.data?.email_sent ? 'Access reset and reset email sent' : 'Access reset — see temp password below')
        : isHealed
        ? 'Existing account healed — password reset and agent linked'
        : (res.data?.email_sent ? 'Access generated and welcome email sent' : 'Access generated (email delivery pending)');
      toast.success(successMsg);
      onGenerated?.(res.data);
    } catch (err) {
      toast.error(getErrorMessage(err, isRegen ? 'Failed to reset access' : 'Failed to generate access'));
    } finally {
      setBusy(false);
    }
  };

  const handleCopy = () => {
    if (!result?.temporary_password) return;
    navigator.clipboard.writeText(result.temporary_password);
    toast.success('Temporary password copied');
  };

  const handleClose = () => {
    onOpenChange(false);
    setResult(null);
  };

  const email = agent?.email || '';
  const npn = agent?.npn || '';
  // In regenerate mode the password is reset on the LINKED portal user,
  // whose login email can differ from the agent profile email. Surface
  // the real login so the admin relays the correct username.
  const loginEmail = isRegen ? (linkedUser?.email || email) : email;
  const emailDiffers = isRegen && linkedUser?.email
    && linkedUser.email.toLowerCase() !== (email || '').toLowerCase();

  return (
    <Dialog open={open} onOpenChange={(v) => { if (!v) handleClose(); else onOpenChange(true); }}>
      <DialogContent data-testid="generate-access-dialog">
        <DialogHeader>
          <DialogTitle className="font-heading flex items-center gap-2">
            <Icon size={18} /> {isRegen ? 'Regenerate Portal Access' : 'Generate Portal Access'}
          </DialogTitle>
          <DialogDescription>
            {isRegen ? (
              <>Reset this agent&apos;s portal password back to their NPN. They will be asked to
              choose a new password on their next login.</>
            ) : (
              <>Instantly create a portal account for this agent. The temporary
              password is the agent&apos;s NPN — they will be asked to change it
              after their first login.</>
            )}
          </DialogDescription>
        </DialogHeader>

        {!result ? (
          <div className="py-3 space-y-3">
            {isRegen && (
              <div
                className="flex items-start gap-2 rounded-md border border-amber-200 bg-amber-50 p-3 text-xs text-amber-900"
                data-testid="regenerate-warning"
              >
                <AlertTriangle size={14} className="mt-0.5 shrink-0" />
                <div>
                  <div className="font-semibold">This will overwrite the agent&apos;s current password.</div>
                  Only proceed if you have verified the request in person or over a trusted channel.
                </div>
              </div>
            )}
            <div className="rounded-lg border border-slate-200 bg-slate-50 p-3 text-sm">
              <div className="text-slate-600 mb-1">
                This will {isRegen ? 'reset the portal password for' : 'create an active portal account for'}:
              </div>
              <div className="font-semibold text-slate-900">{agent?.first_name} {agent?.last_name}</div>
              <div className="text-xs text-slate-600 mt-2 space-y-0.5">
                <div>
                  <span className="font-medium">{isRegen ? 'Login email:' : 'Email:'}</span>{' '}
                  {loginEmail || <span className="text-red-600">(missing)</span>}
                </div>
                <div><span className="font-medium">NPN:</span> {npn || <span className="text-red-600">(missing)</span>}</div>
              </div>
            </div>
            {emailDiffers && (
              <div
                className="flex items-start gap-2 rounded-md border border-sky-200 bg-sky-50 p-2 text-xs text-sky-900"
                data-testid="login-email-differs"
              >
                <AlertCircle size={14} className="mt-0.5 shrink-0" />
                <div>
                  The agent&apos;s portal login is <span className="font-semibold">{loginEmail}</span>,
                  which differs from their profile email ({email}). The agent must sign in with the
                  login email above.
                </div>
              </div>
            )}
            {!loginEmail && (
              <div className="flex items-start gap-2 rounded-md border border-red-200 bg-red-50 p-2 text-xs text-red-800">
                <AlertCircle size={14} className="mt-0.5 shrink-0" />
                This agent has no email on file. Add an email to the agent&apos;s profile before {isRegen ? 'resetting' : 'generating'} access.
              </div>
            )}
            <p className="text-[11px] text-slate-500">
              {isRegen
                ? 'A password-reset notification with the new temporary password will be sent to the agent immediately.'
                : 'A welcome email with the portal link, username and temporary password will be sent to the agent immediately.'}
            </p>
          </div>
        ) : (
          <div className="py-2 space-y-3">
            <div className="flex items-start gap-2 rounded-md border border-emerald-200 bg-emerald-50 p-3 text-sm text-emerald-900">
              <CheckCircle2 size={16} className="mt-0.5 shrink-0" />
              <div>
                <div className="font-semibold">
                  {isRegen ? 'Access reset successfully.' : 'Access generated successfully.'}
                </div>
                <div className="text-xs mt-0.5">
                  {result.email_sent
                    ? (isRegen
                        ? 'The reset notification is on its way to the agent.'
                        : 'The welcome email is on its way to the agent.')
                    : 'Email delivery pending — share the temporary password below with the agent.'}
                </div>
              </div>
            </div>
            <div className="rounded-lg border border-slate-200 bg-white p-3 text-sm space-y-2">
              <div className="text-xs uppercase tracking-wider text-slate-500 font-semibold">Login credentials</div>
              <div>
                <div className="text-[11px] text-slate-500">Username</div>
                <div className="font-mono text-sm text-slate-900" data-testid="generated-username">{result.email}</div>
              </div>
              <div>
                <div className="text-[11px] text-slate-500">Temporary password</div>
                <div className="flex items-center gap-2">
                  <div
                    className="font-mono text-sm text-slate-900 bg-slate-100 px-2 py-1 rounded border border-slate-200 tracking-wide"
                    data-testid="generated-temp-password"
                  >
                    {result.temporary_password}
                  </div>
                  <Button
                    size="sm"
                    variant="outline"
                    onClick={handleCopy}
                    className="gap-1"
                    data-testid="copy-temp-password-btn"
                  >
                    <Copy size={12} /> Copy
                  </Button>
                </div>
                <div className="text-[11px] text-slate-500 mt-1">
                  The agent will be asked to change this on their next login.
                </div>
              </div>
            </div>
          </div>
        )}

        <DialogFooter>
          <Button variant="outline" onClick={handleClose} disabled={busy}>
            {result ? 'Close' : 'Cancel'}
          </Button>
          {!result && (
            <Button
              onClick={handleAction}
              disabled={busy || !loginEmail || !npn}
              variant={isRegen ? 'destructive' : 'default'}
              data-testid={isRegen ? 'regenerate-access-btn' : 'generate-access-btn'}
            >
              <Icon size={16} className="mr-2" />
              {busy
                ? (isRegen ? 'Resetting…' : 'Generating…')
                : (isRegen ? 'Reset Access' : 'Generate Access')}
            </Button>
          )}
        </DialogFooter>
      </DialogContent>
    </Dialog>
  );
};

export default GenerateAccessDialog;
